import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Clock, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export default function ToursList({ type, title, subtitle, heroImg, fallbackPackages = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.listPackages({ type }).then((d) => setItems(d.items || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [type]);

  const display = items.length > 0 ? items : fallbackPackages;
  const filtered = display.filter((p) => {
    const text = `${p.title || ""} ${p.destination || ""} ${p.description || ""}`.toLowerCase();
    return text.includes(q.toLowerCase());
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden">
        <div className="orb orb-blue" style={{ width: 350, height: 350, top: -50, right: -80 }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full text-[10px] font-semibold tracking-[0.3em] text-[#1D4ED8] mb-5">
              <Sparkles className="w-3 h-3" /> {type?.toUpperCase()} COLLECTION
            </div>
            <h1 className="font-display font-bold text-5xl lg:text-7xl text-[#0A1A3A] leading-[1.02]">
              {title.split(" ").map((w, i) => i === Math.floor(title.split(" ").length / 2) ? <span key={i} className="font-serif-italic font-medium text-gradient-blue">{w} </span> : `${w} `)}
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl font-serif-italic leading-relaxed">{subtitle}</p>
            <div className="mt-7 max-w-md glass p-2 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search destinations…" className="pl-9 border-0 bg-transparent text-[#0A1A3A]" data-testid="tours-search-input" />
              </div>
              <Button className="btn-luxe rounded-xl text-white w-full sm:w-auto">Search</Button>
            </div>
          </div>
          <div className="relative h-[420px]">
            <img src={heroImg} className="absolute top-0 right-0 w-[88%] h-[78%] img-notched-tr object-cover shadow-2xl shadow-blue-900/20" alt="" />
            <img src={heroImg} className="absolute bottom-0 left-0 w-[55%] h-[40%] img-notched-bl object-cover shadow-xl shadow-blue-900/20" alt="" />
            <div className="absolute bottom-8 right-8 glass-strong px-5 py-3 rounded-2xl">
              <div className="text-[10px] tracking-[0.2em] text-slate-600">PACKAGES</div>
              <div className="font-display font-bold text-2xl text-gradient-blue">{display.length}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Listing */}
      <section className="py-12 lg:py-16 aurora-section relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="glass p-14 text-center">
              <div className="font-display font-bold text-2xl text-[#0A1A3A]">No packages yet</div>
              <p className="text-slate-600 mt-2 font-serif-italic">Custom itineraries available on request.</p>
              <Link to="/contact"><Button className="mt-5 btn-luxe rounded-xl">Inquire Now</Button></Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filtered.map((p, i) => {
                const variants = ["img-notched-tl", "img-notched-tr", "img-notched-br", "img-notched-bl"];
                return (
                  <motion.div
                    key={p.id || i}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                  >
                    <div className="glass glass-hover p-3 h-full flex flex-col transition-all duration-300">
                      <div className={`relative h-56 overflow-hidden ${variants[i % variants.length]} group`}>
                        <img src={p.image_url || "https://images.pexels.com/photos/2387873/pexels-photo-2387873.jpeg"} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1A3A]/50 to-transparent" />
                        {p.is_featured && (
                          <div className="absolute top-4 right-4 glass-dark px-3 py-1 rounded-full text-[10px] tracking-wider text-amber-300">
                            ★ FEATURED
                          </div>
                        )}
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                          {p.destination && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{p.destination}</span>}
                          {p.duration && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.duration}</span>}
                        </div>
                        <h3 className="font-display font-bold text-xl text-[#0A1A3A] leading-tight">{p.title}</h3>
                        <p className="text-sm text-slate-600 mt-2 font-serif-italic line-clamp-2 flex-1">{p.short_description || p.description}</p>
                        <div className="mt-5 pt-4 border-t border-white/60 flex items-end justify-between">
                          <div>
                            {p.price ? (
                              <>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500">From</div>
                                <div className="font-display font-bold text-2xl text-gradient-blue">₹{Number(p.price).toLocaleString("en-IN")}</div>
                                {p.price_label && <div className="text-[10px] text-slate-500">{p.price_label}</div>}
                              </>
                            ) : <div className="text-sm text-slate-500 font-serif-italic">Price on Request</div>}
                          </div>
                          <Link to={p.id ? `/package/${p.id}` : "/contact"}>
                            <Button size="sm" className="btn-luxe rounded-xl text-white" data-testid={`package-card-cta-${i}`}>
                              Details <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
